import React, { useState, useEffect, useRef } from 'react';
import { Heart, Phone, Clock, Shield, CheckCircle, Star, AlertCircle } from 'lucide-react';
import { Helmet } from 'react-helmet';
import GlobalNavigation from '../../components/GlobalNavigation';
import { getDynamicPhoneNumber } from '../../services/constants';

// Import components
import EmergencyBailAssistanceForm from './components/EmergencyBailAssistanceForm';
import SocialProofRow from './components/SocialProofRow';
import TrustBadges from './components/TrustBadges';

const LandingPage = () => {
  const [fallbackPhone, setFallbackPhone] = useState('+19162263934');
  const [menuOpen, setMenuOpen] = useState(false);
  const formRef = useRef(null);

  // Set up dynamic fallback phone number
  useEffect(() => {
    const updateFallbackPhone = async () => {
      try {
        const dynamicPhone = await getDynamicPhoneNumber();
        setFallbackPhone(dynamicPhone);
      } catch (error) {
        setFallbackPhone('+19162263934');
      }
    };

    updateFallbackPhone();

    const handleCurrentAgentChange = () => {
      updateFallbackPhone();
    };

    window.addEventListener('currentAgentChanged', handleCurrentAgentChange);

    return () => {
      window.removeEventListener('currentAgentChanged', handleCurrentAgentChange);
    };
  }, []);

  const formatPhoneDisplay = (phone) => {
    // Ensure phone is a string and has content
    if (!phone || typeof phone !== 'string') {
      return '(916) 226-3934'; // Default fallback
    }
    return phone?.replace('+1', '')?.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  };

  // Structured data for reviews
  const reviewsStructuredData = {
    "@context": "https://schema.org",
    "@type": "Review",
    "itemReviewed": {
      "@type": "LocalBusiness",
      "name": "Sailors Bail Bonds"
    },
    "reviewRating": {
      "@type": "Rating", 
      "ratingValue": "5",
      "bestRating": "5"
    },
    "author": {
      "@type": "Person",
      "name": "Maria S."
    },
    "reviewBody": "I was crying and shaking when I called. They calmed me down, explained everything in simple words, and had my daughter home in 3 hours. I'll never forget their kindness."
  };

  const handleCallNow = () => {
    window.location.href = 'tel:9162263934';
  };

  const handleStartBailOnline = () => {
    if (formRef?.current) {
      formRef?.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleTextAgent = () => {
    window.location.href = 'sms:9162263934';
  };

  return (
    <>
      <Helmet>
        {/* Dynamic SEO optimization */}
        <title>Sailors Bail Bonds - 24/7 Emergency Bail Services | Fast Release in Sacramento, Stockton, Modesto</title>
        <meta name="description" content="Get your loved one home soon! 24/7 bail bond services with 2-4 hour release time. Payment plans from $50. Licensed agents serving Sacramento, Stockton, Modesto. Call now!" />
        
        {/* Enhanced keywords for AEO */}
        <meta name="keywords" content="emergency bail bonds, 24/7 bail services, fast jail release, Sacramento bail bonds, Stockton bail bondsman, Modesto bail agent, cheap bail bonds, payment plans, licensed bail bonds, family crisis help, jail release services, Central Valley bail bonds" />
        
        {/* Additional AEO-focused meta tags */}
        <meta name="geo.region" content="US-CA" />
        <meta name="geo.placename" content="Sacramento, Stockton, Modesto" />
        <meta name="ICBM" content="38.5767, -121.4934" />
        
        {/* Rich snippets for local business */}
        <script type="application/ld+json">
          {JSON.stringify(reviewsStructuredData)}
        </script>
        
        {/* Hreflang for local targeting */}
        <link rel="alternate" href="https://sailorsbailbonds.com/" hreflang="en-US" />
        <link rel="alternate" href="https://sailorsbailbonds.com/" hreflang="x-default" />
      </Helmet>
      <div className="min-h-screen bg-white">
        {/* Global Navigation */}
        <GlobalNavigation 
          phone={formatPhoneDisplay(fallbackPhone)}
          onMenuClick={() => setMenuOpen(true)}
        />

        {/* Crisis Support Banner - Emergency Bail Service Active */}
        <section className="animate-emergency-police text-white py-3 px-4 shadow-lg" role="banner" aria-label="Emergency bail service contact banner">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-center justify-center text-center space-y-2 sm:space-y-0 sm:space-x-6">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 animate-police-light-dot" aria-hidden="true" />
                <span className="font-bold">Emergency Bail Service Active</span>
              </div>
              <div className="text-sm opacity-90">Someone you love arrested? We're here to help.</div>
              <a 
                href={`tel:${fallbackPhone}`}
                className="bg-white text-red-600 px-6 py-2 rounded-full font-bold hover:bg-red-50 transition-colors text-sm"
                aria-label={`Call emergency bail service at ${formatPhoneDisplay(fallbackPhone)}`}
              >
                Call Now: {formatPhoneDisplay(fallbackPhone)}
              </a>
            </div>
          </div>
        </section>

        {/* Current Clients Access Section - HIGHLY VISIBLE */}
        <section className="bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600 text-white py-6 px-4 border-b-4 border-yellow-400 shadow-xl" role="banner" aria-label="Current clients account access">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center justify-center text-center space-y-4 lg:space-y-0 lg:space-x-8">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-400 rounded-full mr-3 animate-pulse" aria-hidden="true"></div>
                <span className="font-bold text-xl">CURRENT CLIENTS</span>
              </div>
              
              <div className="text-lg font-medium">
                Access Your Bail Account Information Online
              </div>
              
              <a 
                href="https://agents.simplybail.com/m/sailorsbailbonds43/office?code"
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-gradient-to-r from-yellow-400 to-amber-500 text-black px-8 py-4 rounded-2xl font-black text-lg hover:from-yellow-300 hover:to-amber-400 transition-all transform hover:scale-105 shadow-2xl border-4 border-yellow-300 hover:border-yellow-200 flex items-center min-w-max"
                aria-label="Access your bail account information on Simplybail.com"
              >
                <Shield className="w-6 h-6 mr-3 group-hover:animate-pulse" aria-hidden="true" />
                <span className="whitespace-nowrap">ACCESS YOUR BAIL ACCOUNT</span>
              </a>
            </div>
            
            {/* Additional Emphasis for Mobile */}
            <div className="lg:hidden mt-4 text-center">
              <div className="text-sm text-blue-100 font-medium">
                ⬆️ Click the button above to check your account status ⬆️
              </div>
            </div>
          </div>
        </section>

        {/* Hero Section - Enhanced for AEO */}
        <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-white overflow-hidden" role="main">
          {/* Background Images - Family Reunion Pictures */}
          <div className="absolute inset-0" aria-hidden="true">
            {/* Family Hug Image Background */}
            <div 
              className="absolute top-0 right-0 w-1/3 h-full opacity-15 bg-cover bg-center"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1609220136736-443140cffec6?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80')`,
                backgroundBlendMode: 'overlay'
              }}
              alt="Emotional family reunion after jail release"
            ></div>
            
            {/* Mother-Child Reunion Image */}
            <div 
              className="absolute bottom-0 left-0 w-1/2 h-2/3 opacity-10 bg-cover bg-center"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1544717297-fa95b6ee9643?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80')`,
                backgroundBlendMode: 'overlay'
              }}
              alt="Mother embracing child in emotional reunion"
            ></div>
          </div>

          {/* Background pattern overlay */}
          <div className="absolute inset-0 opacity-10 z-10" aria-hidden="true">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 25% 25%, white 2px, transparent 2px)',
              backgroundSize: '50px 50px'
            }}></div>
          </div>

          <div className="relative z-20 max-w-7xl mx-auto px-6 py-16 lg:py-20">
            <div className="text-center max-w-5xl mx-auto">
              {/* Clear Above-the-Fold Hero Section */}
              <div className="mb-12">
                {/* Primary Headline - What We Do */}
                <h1 className="text-4xl lg:text-6xl font-black mb-6 leading-tight text-white">
                  24/7 Emergency Bail Bond Services
                </h1>
                
                {/* Subheadline - Who We Help & Main Benefit */}
                <p className="text-xl lg:text-2xl mb-8 text-blue-100 font-semibold leading-relaxed max-w-4xl mx-auto">
                  We help families in crisis get their loved ones released from jail fast—usually within 2-4 hours with flexible payment plans starting at just $50
                </p>

                {/* Primary Call to Action */}
                <div className="mb-10">
                  <a 
                    href={`tel:${fallbackPhone}`}
                    className="group inline-flex items-center bg-gradient-to-r from-yellow-400 to-amber-500 text-black px-10 lg:px-14 py-5 lg:py-6 rounded-2xl font-black text-xl lg:text-2xl hover:from-yellow-300 hover:to-amber-400 transition-all transform hover:scale-105 shadow-2xl"
                    aria-label="Call sailors bail bond service for immediate help"
                  >
                    <Phone className="w-7 h-7 lg:w-8 lg:h-8 mr-3 group-hover:animate-pulse" aria-hidden="true" />
                    Get Started - Call Now
                  </a>
                  <div className="mt-4 text-blue-200 text-base font-medium">
                    Licensed Agent on Duty 24/7: {formatPhoneDisplay(fallbackPhone)}
                  </div>
                </div>

                {/* Three Supporting Bullets/Icons */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                  <div className="flex flex-col items-center p-6 bg-white/10 rounded-xl backdrop-blur-sm border border-white/20">
                    <Clock className="w-10 h-10 text-yellow-300 mb-3" aria-hidden="true" />
                    <h3 className="font-bold text-lg mb-1 text-white">Fast 2-4 Hour Release</h3>
                    <p className="text-blue-200 text-sm">Most releases completed same day</p>
                  </div>
                  
                  <div className="flex flex-col items-center p-6 bg-white/10 rounded-xl backdrop-blur-sm border border-white/20">
                    <Heart className="w-10 h-10 text-yellow-300 mb-3" aria-hidden="true" />
                    <h3 className="font-bold text-lg mb-1 text-white">Payment Plans from $50</h3>
                    <p className="text-blue-200 text-sm">Flexible financing for any budget</p>
                  </div>
                  
                  <div className="flex flex-col items-center p-6 bg-white/10 rounded-xl backdrop-blur-sm border border-white/20">
                    <Shield className="w-10 h-10 text-yellow-300 mb-3" aria-hidden="true" />
                    <h3 className="font-bold text-lg mb-1 text-white">Licensed & Available 24/7</h3>
                    <p className="text-blue-200 text-sm">15+ years serving Sacramento area</p>
                  </div>
                </div>
              </div>

              {/* Trust Badge */}
              <div className="inline-flex items-center bg-green-500/20 border border-green-400 text-green-100 px-6 py-3 rounded-full backdrop-blur-sm" role="note">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-3 animate-pulse" aria-hidden="true"></div>
                Serving Sacramento, Stockton, Modesto & All Central Valley Counties
              </div>
            </div>
          </div>
        </section>

        {/* Social Proof Row */}
        <SocialProofRow />

        {/* Emergency Bail Bond Assistance Form - Replaces "Get Started in 60 Seconds" */}
        <section ref={formRef} className="py-20 bg-gradient-to-br from-red-50 to-orange-50">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-12">
              <div className="inline-flex items-center bg-red-100 text-red-800 px-6 py-3 rounded-full mb-6 border-2 border-red-300">
                <span className="w-3 h-3 bg-red-500 rounded-full mr-3 animate-pulse"></span>
                <span className="font-bold text-lg">🚨 EMERGENCY ASSISTANCE AVAILABLE NOW</span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-6">
                Emergency Bail Bond Assistance
              </h2>
              <p className="text-xl lg:text-2xl text-gray-700 leading-relaxed max-w-4xl mx-auto">
                <strong className="text-red-600">Your loved one needs help right now.</strong> Our licensed agents are standing by 
                24/7 to provide immediate emergency bail assistance. Complete the form below and get contacted 
                within 2 minutes.
              </p>
            </div>
            
            <EmergencyBailAssistanceForm />
            
            {/* Emergency Support Information */}
            <div className="mt-12 text-center">
              <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white p-8 rounded-2xl shadow-2xl">
                <h3 className="text-2xl font-bold mb-4">
                  🚨 Emergency Bail Bond Services Available 24/7
                </h3>
                <p className="text-xl text-blue-100 mb-6 leading-relaxed">
                  When someone you love is arrested, every minute counts. Our emergency response team is 
                  trained to handle urgent situations with compassion and speed.
                </p>
                <div className="grid md:grid-cols-3 gap-6 text-center">
                  <div className="bg-white/10 p-6 rounded-xl">
                    <div className="text-3xl mb-2">⚡</div>
                    <div className="font-bold text-lg mb-2">2-Minute Response</div>
                    <div className="text-blue-200">Emergency priority contact</div>
                  </div>
                  <div className="bg-white/10 p-6 rounded-xl">
                    <div className="text-3xl mb-2">💰</div>
                    <div className="font-bold text-lg mb-2">Payment Plans</div>
                    <div className="text-blue-200">Emergency financing available</div>
                  </div>
                  <div className="bg-white/10 p-6 rounded-xl">
                    <div className="text-3xl mb-2">🏛️</div>
                    <div className="font-bold text-lg mb-2">All Counties</div>
                    <div className="text-blue-200">Statewide emergency coverage</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section - Enhanced for AEO */}
        <section className="py-20 bg-gray-50 relative overflow-hidden" role="region" aria-labelledby="faq-heading">
          {/* Background Family Images */}
          <div className="absolute inset-0" aria-hidden="true">
            {/* Family Walking Away Together */}
            <div 
              className="absolute top-0 left-0 w-1/4 h-full opacity-5 bg-cover bg-center"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1511895426328-dc8714191300?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80')`,
                backgroundBlendMode: 'multiply'
              }}
              alt="Happy family walking together after successful bail bond process"
            ></div>
            
            {/* Couple Embracing */}
            <div 
              className="absolute bottom-0 right-0 w-1/3 h-2/3 opacity-8 bg-cover bg-center"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1516627145497-ae4c91e5974d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80')`,
                backgroundBlendMode: 'multiply'
              }}
              alt="Emotional couple reunion after jail release"
            ></div>
          </div>

          <div className="relative z-10 max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 id="faq-heading" className="text-3xl lg:text-4xl font-black text-gray-900 mb-6">
                Common Questions About Sailors Bail Bonds
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
                We understand you're scared and confused. These are the questions every family asks us.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 mb-16" role="list" aria-label="Frequently asked questions">
              {[
                {
                  question: "How fast can you get someone out of jail?",
                  answer: "Most people are released within 2-4 hours of posting bail. We work quickly to complete all paperwork and coordinate with the facility for the fastest possible release. Some cases may take longer depending on the facility's processing time.",
                  icon: "⏰",
                  category: "Timing"
                },
                {
                  question: "What if I can't afford the full bail amount?",
                  answer: "We offer flexible payment plans starting as low as $50 down. We work with your budget to find a payment solution that works for your family's financial situation. No one should stay in jail because of money.",
                  icon: "💰",
                  category: "Payment"
                },
                {
                  question: "Are you really available 24/7?",
                  answer: "Yes, we are available 24 hours a day, 7 days a week, 365 days a year. Arrests don't happen on a schedule, so neither do we. Call anytime for immediate assistance from a licensed agent.",
                  icon: "🕐",
                  category: "Availability"
                },
                {
                  question: "What information do I need to get started?",
                  answer: "Just their full name and the jail where they're being held. We'll find all the other details and handle all the paperwork. You don't need to understand the legal process - that's our job.",
                  icon: "📋",
                  category: "Process"
                }
              ]?.map((faq, index) => (
                <article key={index} className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow border-l-4 border-blue-500" role="listitem">
                  <div className="flex items-start space-x-4">
                    <span className="text-4xl flex-shrink-0 mt-1" aria-hidden="true">{faq?.icon}</span>
                    <div>
                      <h3 className="text-xl font-bold text-blue-800 mb-3">
                        {faq?.question}
                      </h3>
                      <p className="text-gray-700 text-lg leading-relaxed mb-4">
                        {faq?.answer}
                      </p>
                      <a 
                        href={`tel:${fallbackPhone}`}
                        className="text-blue-600 hover:text-blue-800 font-semibold flex items-center group"
                        aria-label={`Call about ${faq?.category?.toLowerCase()} questions`}
                      >
                        Ask us about {faq?.category?.toLowerCase()}
                        <Phone className="w-4 h-4 ml-1 group-hover:animate-pulse" aria-hidden="true" />
                      </a>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {/* Emergency CTA */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-8 rounded-2xl text-center" role="complementary">
              <h3 className="text-2xl font-bold mb-4">
                Still Have Questions? We're Here to Help.
              </h3>
              <p className="text-xl text-blue-100 mb-6">
                Call us now and speak to a licensed agent who will explain everything in simple terms.
              </p>
              <a 
                href={`tel:${fallbackPhone}`}
                className="bg-yellow-500 hover:bg-yellow-400 text-black px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 inline-flex items-center"
                aria-label="Speak to licensed bail bond agent now"
              >
                <Phone className="w-5 h-5 mr-2" aria-hidden="true" />
                Speak to Agent: {formatPhoneDisplay(fallbackPhone)}
              </a>
            </div>
          </div>
        </section>

        {/* Testimonials Section - Enhanced for AEO */}
        <section className="py-20 bg-white relative overflow-hidden" role="region" aria-labelledby="testimonials-heading">
          {/* Background Family Images */}
          <div className="absolute inset-0" aria-hidden="true">
            {/* Multi-generational Family Portrait */}
            <div 
              className="absolute top-1/4 left-0 w-1/3 h-1/2 opacity-6 bg-cover bg-center"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1566430363739-b2986e628ffe?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80')`,
                backgroundBlendMode: 'multiply'
              }}
              alt="Multi-generational family portrait showing family unity"
            ></div>
            
            {/* Happy Family Silhouette */}
            <div 
              className="absolute bottom-0 right-0 w-2/5 h-3/4 opacity-4 bg-cover bg-center"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1542103749-8ef59b94f47e?ixlib=rb-4.0.3&auto=format&w=800&q=80')`,
                backgroundBlendMode: 'multiply'
              }}
              alt="Silhouette of happy family celebrating together"
            ></div>
          </div>

          <div className="relative z-10 max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 id="testimonials-heading" className="text-3xl lg:text-4xl font-black text-gray-900 mb-6">
                Real Families We've Helped in Sacramento, Stockton & Modesto
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Every week, we help parents, spouses, and children navigate this difficult situation with compassion and expertise.
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8 mb-16" role="list" aria-label="Customer testimonials">
              {[
                {
                  quote: "I was crying and shaking when I called at 2 AM. They calmed me down, explained everything in simple words, and had my daughter home in 3 hours. I'll never forget their kindness during our family crisis.",
                  name: "Maria S.",
                  role: "Mother from Sacramento",
                  rating: 5,
                  location: "Sacramento County"
                },
                {
                  quote: "2 AM arrest call - I panicked and didn't know what to do. They answered immediately, walked me through each step, and my son was home before breakfast. Professional and caring service when we needed it most.",
                  name: "David R.", 
                  role: "Father from Stockton",
                  rating: 5,
                  location: "San Joaquin County"
                },
                {
                  quote: "I had no money, no credit, nothing. They worked with me on a payment plan for just $50 down and got my husband out the same day. They treat you like family, not just another case number.",
                  name: "Jennifer L.",
                  role: "Wife from Modesto", 
                  rating: 5,
                  location: "Stanislaus County"
                }
              ]?.map((testimonial, index) => (
                <article key={index} className="bg-gray-50 p-8 rounded-2xl shadow-lg" role="listitem">
                  <div className="flex mb-4" role="img" aria-label={`${testimonial?.rating} star review`}>
                    {[...Array(testimonial?.rating)]?.map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-500 fill-current" aria-hidden="true" />
                    ))}
                  </div>
                  <blockquote className="text-gray-700 text-lg leading-relaxed mb-6 italic">
                    "{testimonial?.quote}"
                  </blockquote>
                  <footer className="border-t border-gray-200 pt-4">
                    <div className="font-bold text-gray-900">{testimonial?.name}</div>
                    <div className="text-gray-600">{testimonial?.role}</div>
                    <div className="text-sm text-blue-600 font-medium">{testimonial?.location}</div>
                  </footer>
                </article>
              ))}
            </div>

            {/* Service Statistics */}
            <div className="bg-blue-900 text-white p-12 rounded-2xl" role="region" aria-labelledby="stats-heading">
              <h3 id="stats-heading" className="sr-only">Service Statistics</h3>
              <div className="grid md:grid-cols-4 gap-8 text-center" role="list" aria-label="Service statistics">
                <div role="listitem">
                  <div className="text-4xl font-black text-yellow-300 mb-2">2,000+</div>
                  <div className="text-blue-200">Families Helped Annually</div>
                </div>
                <div role="listitem">
                  <div className="text-4xl font-black text-yellow-300 mb-2">24/7</div>
                  <div className="text-blue-200">Always Available Service</div>
                </div>
                <div role="listitem">
                  <div className="text-4xl font-black text-yellow-300 mb-2">15+</div>
                  <div className="text-blue-200">Years Licensed Experience</div>
                </div>
                <div role="listitem">
                  <div className="text-4xl font-black text-yellow-300 mb-2">3 hrs</div>
                  <div className="text-blue-200">Average Release Time</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Service Areas Section - Critical for Local SEO */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50 relative overflow-hidden" role="region" aria-labelledby="service-areas-heading">
          <div className="relative z-10 max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 id="service-areas-heading" className="text-3xl lg:text-4xl font-black text-gray-900 mb-6">
                Serving Central Valley & Northern California Counties 24/7
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
                Licensed bail bond agents available in all major Central Valley and Northern California facilities and counties.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12" role="list" aria-label="Service area coverage">
              {[
                {
                  region: "Central Valley Counties",
                  counties: [
                    {
                      name: "San Joaquin County",
                      facilities: ["San Joaquin County Jail", "Stockton City Jail", "French Camp Facility"],
                      responseTime: "45 minutes average"
                    },
                    {
                      name: "Stanislaus County",
                      facilities: ["Stanislaus County Jail", "Modesto City Jail", "Public Safety Center"],
                      responseTime: "1 hour average"
                    }
                  ]
                },
                {
                  region: "Northern California Counties",
                  counties: [
                    {
                      name: "Sacramento County",
                      facilities: ["Sacramento County Main Jail", "Rio Cosumnes Correctional Center", "Sacramento City Jail"],
                      responseTime: "30 minutes average"
                    },
                    {
                      name: "El Dorado County",
                      facilities: ["El Dorado County Jail", "South Lake Tahoe Facility"],
                      responseTime: "1.5 hours average"
                    },
                    {
                      name: "Placer County",
                      facilities: ["Placer County Jail", "Auburn Justice Center"],
                      responseTime: "1 hour average"
                    },
                    {
                      name: "Yolo County",
                      facilities: ["Yolo County Jail", "Woodland Detention Facility"],
                      responseTime: "1 hour average"
                    },
                    {
                      name: "Yuba County",
                      facilities: ["Yuba County Jail", "Marysville Detention Center"],
                      responseTime: "1.5 hours average"
                    }
                  ]
                }
              ]?.map((region, regionIndex) => (
                <div key={regionIndex} className="space-y-8" role="listitem">
                  <div className="text-center">
                    <h3 className="text-2xl font-black text-gray-900 mb-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-xl">
                      {region?.region}
                    </h3>
                  </div>
                  
                  <div className="space-y-6">
                    {region?.counties?.map((county, countyIndex) => (
                      <article key={countyIndex} className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-blue-500">
                        <h4 className="text-xl font-bold text-gray-900 mb-3">
                          {county?.name}
                        </h4>
                        
                        <div className="text-gray-600 text-sm mb-4">
                          <div className="font-semibold mb-2">Major Facilities:</div>
                          <ul className="space-y-1">
                            {county?.facilities?.map((facility, facilityIdx) => (
                              <li key={facilityIdx}>{facility}</li>
                            ))}
                          </ul>
                        </div>
                        
                        <div className="inline-flex items-center bg-green-100 text-green-800 px-3 py-2 rounded-full font-semibold text-sm">
                          <CheckCircle className="w-4 h-4 mr-2" aria-hidden="true" />
                          {county?.responseTime}
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-16 text-center">
              <div className="bg-blue-900 text-white p-8 rounded-2xl">
                <h3 className="text-2xl font-bold mb-4">Complete Coverage Across All Counties</h3>
                <p className="text-blue-100 text-lg mb-6">
                  Licensed to serve in all Central Valley and Northern California counties with fast response times and local expertise.
                </p>
                <a 
                  href={`tel:${fallbackPhone}`}
                  className="bg-yellow-500 hover:bg-yellow-400 text-black px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 inline-flex items-center"
                  aria-label="Call for service in any county"
                >
                  <Phone className="w-5 h-5 mr-2" aria-hidden="true" />
                  Call for Any County: {formatPhoneDisplay(fallbackPhone)}
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Office Location Section with Map */}
        <section className="py-20 bg-white relative overflow-hidden" role="region" aria-labelledby="location-heading">
          <div className="relative z-10 max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 id="location-heading" className="text-3xl lg:text-4xl font-black text-gray-900 mb-6">
                Visit Our Downtown Sacramento Office
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
                Need to meet in person? Our central Downtown Sacramento office is easy to find with convenient parking and accessible from all major highways.
              </p>
            </div>

            {/* Office Building Photo Section */}
            <div className="mb-12">
              <div className="max-w-4xl mx-auto">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-2xl border border-blue-200 shadow-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-6 text-center flex items-center justify-center">
                    <span className="mr-3">🏢</span>
                    Our Professional Downtown Sacramento Office Building
                  </h3>
                  
                  <div className="relative rounded-xl overflow-hidden shadow-lg">
                    <img
                      src="assets/images/Image_10-29-25_at_6.11_AM-1761743920882.jpeg"
                      alt="Modern multi-story office building exterior where Sailors Bail Bonds is located at 901 H Street Suite 103, showing professional downtown Sacramento location with distinctive curved architectural elements and ground floor retail spaces"
                      className="w-full h-64 md:h-80 object-cover"
                      loading="lazy"
                    />
                    
                    {/* Photo overlay with location info */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                      <div className="text-white">
                        <div className="text-lg font-bold mb-2">📍 Sailors Bail Bonds</div>
                        <div className="text-sm opacity-90">901 H Street, Suite 103 • Downtown Sacramento, CA 95814</div>
                        <div className="text-xs opacity-75 mt-1">Professional, clean office environment • Easy parking available • Handicap accessible</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-center mt-4">
                    <p className="text-gray-700 text-sm">
                      <strong>Easy to locate:</strong> Look for this distinctive multi-story building in downtown Sacramento. 
                      We're in Suite 103 with convenient street-level access and free parking nearby.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-start">
              {/* Office Information */}
              <div className="space-y-8">
                {/* Address Card */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-2xl border border-blue-200 shadow-lg">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                    Sailors Bail Bonds Office
                  </h3>
                  
                  <div className="space-y-4 text-lg">
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <span className="text-white text-sm">📍</span>
                      </div>
                      <div>
                        <div className="font-bold text-gray-900">901 H Street, Suite 103</div>
                        <div className="text-gray-700">Downtown Sacramento, California 95814</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                        <Phone className="w-3 h-3 text-white" aria-hidden="true" />
                      </div>
                      <a 
                        href={`tel:${fallbackPhone}`}
                        className="font-bold text-blue-600 hover:text-blue-800 transition-colors"
                        aria-label="Call sailors bail bonds office"
                      >
                        {formatPhoneDisplay(fallbackPhone)}
                      </a>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center">
                        <Clock className="w-3 h-3 text-white" aria-hidden="true" />
                      </div>
                      <div className="text-gray-700">
                        <span className="font-semibold">24/7 Emergency Service</span>
                        <div className="text-sm text-gray-600">Licensed agents always available</div>
                      </div>
                    </div>
                  </div>

                  {/* Mobile-First Action Buttons */}
                  <div className="mt-8 space-y-4">
                    {/* Get Directions Button - Works on all devices */}
                    <a
                      href="https://www.google.com/maps/dir/?api=1&destination=901+H+Street+Suite+103+Sacramento+CA+95814"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-blue-800 transition-all transform hover:scale-105 shadow-lg flex items-center justify-center"
                      aria-label="Get driving directions to Sailors Bail Bonds office"
                    >
                      <span className="mr-3 text-xl">🗺️</span>
                      Get Directions
                    </a>
                    
                    {/* Emergency Call Button */}
                    <a 
                      href={`tel:${fallbackPhone}`}
                      className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-4 rounded-xl font-bold text-lg hover:from-red-700 hover:to-red-800 transition-all transform hover:scale-105 shadow-lg flex items-center justify-center"
                      aria-label="Call sailors bail bonds emergency line"
                    >
                      <Phone className="w-5 h-5 mr-3" aria-hidden="true" />
                      Call Now - 24/7 Service
                    </a>
                  </div>
                </div>

                {/* Office Features */}
                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">Office Features</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm" role="list">
                    <div className="flex items-center" role="listitem">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2" aria-hidden="true" />
                      <span>Free Parking</span>
                    </div>
                    <div className="flex items-center" role="listitem">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2" aria-hidden="true" />
                      <span>Easy parking available</span>
                    </div>
                    <div className="flex items-center" role="listitem">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2" aria-hidden="true" />
                      <span>Handicap accessible</span>
                    </div>
                    <div className="flex items-center" role="listitem">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2" aria-hidden="true" />
                      <span>Wheelchair Accessible</span>
                    </div>
                    <div className="flex items-center" role="listitem">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2" aria-hidden="true" />
                      <span>Private Consultation</span>
                    </div>
                    <div className="flex items-center" role="listitem">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2" aria-hidden="true" />
                      <span>Downtown Location</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Interactive Map */}
              <div className="space-y-6">
                {/* Embedded Google Map */}
                <div className="bg-gray-100 rounded-2xl overflow-hidden shadow-lg border border-gray-200">
                  <div className="relative">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3118.515776998779!2d-121.49659!3d38.5767!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x809ad0e8cecd5a57%3A0x87c2d8b8c0f1e7e5!2s901%20H%20St%20%23103%2C%20Sacramento%2C%20CA%2095814!5e0!3m2!1sen!2sus!4v1698765432100!5m2!1sen!2sus"
                      width="100%"
                      height="400"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Sailors Bail Bonds Office Location - 901 H Street Suite 103, Sacramento CA 95814"
                      aria-label="Interactive map showing Sailors Bail Bonds office location in downtown Sacramento"
                    ></iframe>
                    
                    {/* Map Overlay for Mobile Enhancement */}
                    <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg">
                      <div className="text-sm font-bold text-gray-900">📍 Downtown Sacramento Office</div>
                      <div className="text-xs text-gray-600">901 H St, Suite 103</div>
                    </div>
                  </div>
                </div>

                {/* Additional Location Info */}
                <div className="bg-gradient-to-r from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
                  <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <span className="mr-2">🚗</span>
                    Easy to Find
                  </h4>
                  <div className="space-y-3 text-sm text-gray-700">
                    <div className="flex items-start space-x-2">
                      <span className="font-semibold text-green-700 mt-0.5">•</span>
                      <span><strong>From I-5:</strong> Take J Street exit, go east to 9th Street, turn right on H Street</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <span className="font-semibold text-green-700 mt-0.5">•</span>
                      <span><strong>From US-50:</strong> Take Capitol Mall exit, turn left on 9th Street, right on H Street</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <span className="font-semibold text-green-700 mt-0.5">•</span>
                      <span><strong>Public Transit:</strong> RT Bus Lines 30, 38 stop nearby</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile-Optimized Action Strip */}
            <div className="mt-16 lg:hidden">
              <div className="bg-blue-900 text-white p-6 rounded-2xl">
                <h3 className="text-xl font-bold mb-4 text-center">Need Help Right Now?</h3>
                <div className="grid grid-cols-1 gap-4">
                  <a
                    href="https://www.google.com/maps/dir/?api=1&destination=901+H+Street+Suite+103+Sacramento+CA+95814"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white text-blue-900 px-6 py-3 rounded-xl font-bold text-center hover:bg-blue-50 transition-colors flex items-center justify-center"
                    aria-label="Open GPS navigation to sailors bail bonds office"
                  >
                    <span className="mr-2">📱</span>
                    Open in Maps App
                  </a>
                  
                  <a 
                    href={`tel:${fallbackPhone}`}
                    className="bg-red-600 text-white px-6 py-3 rounded-xl font-bold text-center hover:bg-red-700 transition-colors flex items-center justify-center"
                    aria-label="Call sailors bail bonds emergency service"
                  >
                    <Phone className="w-5 h-5 mr-2" aria-hidden="true" />
                    Emergency: {formatPhoneDisplay(fallbackPhone)}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Badges */}
        <TrustBadges />

        {/* What We Do in First 10 Minutes */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-black text-gray-900 mb-6">
                What We Do for You in the First 10 Minutes
              </h2>
            </div>
            
            <div className="grid lg:grid-cols-5 gap-6">
              {[
                { step: "1", title: "Confirm charges & bail amount", icon: "🔍" },
                { step: "2", title: "Send you a secure link to sign", icon: "📱" },
                { step: "3", title: "Arrange payment", icon: "💳" },
                { step: "4", title: "Notify the jail and begin release", icon: "🏛️" },
                { step: "5", title: "Text you updates", icon: "💬" }
              ]?.map((item, index) => (
                <div key={index} className="text-center p-6 bg-blue-50 rounded-xl border border-blue-200">
                  <div className="text-4xl mb-4">{item?.icon}</div>
                  <div className="text-2xl font-bold text-blue-600 mb-2">{item?.step}</div>
                  <h3 className="text-lg font-semibold text-gray-900">{item?.title}</h3>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Risk Reversal Section */}
        <section className="py-16 bg-green-50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-black text-gray-900 mb-6">
                Our Guarantee to You
              </h2>
            </div>
            
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="text-center p-8 bg-white rounded-2xl shadow-lg">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">No Obligation Consultation</h3>
                <p className="text-gray-600">If you don't proceed, you pay nothing.</p>
              </div>
              
              <div className="text-center p-8 bg-white rounded-2xl shadow-lg">
                <Shield className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">Transparent Pricing</h3>
                <p className="text-gray-600">No junk fees. What we quote is what you pay.</p>
              </div>
              
              <div className="text-center p-8 bg-white rounded-2xl shadow-lg">
                <Star className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">Rate Match Promise</h3>
                <p className="text-gray-600">We'll match any written rate from a licensed Sacramento agency.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Final Emergency CTA */}
        <section className="py-20 bg-gradient-to-r from-red-600 via-red-500 to-orange-500 text-white relative overflow-hidden" role="region" aria-labelledby="final-cta-heading">
          {/* Background Images */}
          <div className="absolute inset-0" aria-hidden="true">
            {/* Family Reunion Joy */}
            <div 
              className="absolute inset-0 opacity-10 bg-cover bg-center"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1609220136736-443140cffec6?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80')`,
                backgroundBlendMode: 'overlay'
              }}
              alt="Joyful family reunion celebration after successful bail bond process"
            ></div>
          </div>

          <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
            <div className="mb-8">
              <AlertCircle className="w-16 h-16 mx-auto mb-6 animate-pulse" aria-hidden="true" />
              <h2 id="final-cta-heading" className="text-4xl lg:text-6xl font-black mb-6">
                Don't Wait - Call Now
              </h2>
              <p className="text-2xl lg:text-3xl mb-4 leading-tight">
                Every minute in jail feels like hours to your loved one
              </p>
              <p className="text-xl lg:text-2xl text-red-100 mb-12">
                We can have them home soon. Licensed agents standing by 24/7.
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-6 justify-center items-center">
              <a 
                href={`tel:${fallbackPhone}`}
                className="group bg-white text-red-600 px-8 lg:px-12 py-5 lg:py-6 rounded-2xl font-black text-xl lg:text-2xl hover:bg-red-50 transition-all transform hover:scale-105 shadow-2xl flex items-center w-full lg:w-auto justify-center"
                aria-label="Call sailors bail bond service immediately"
              >
                <Phone className="w-8 h-8 mr-4 group-hover:animate-bounce" aria-hidden="true" />
                Call Immediately: {formatPhoneDisplay(fallbackPhone)}
              </a>
              
              <div className="text-red-100 font-bold">Licensed Agents Available Now</div>
            </div>

            <div className="mt-12 bg-white/20 rounded-2xl p-8 backdrop-blur-sm" role="complementary">
              <div className="grid md:grid-cols-2 gap-6 text-lg" role="list" aria-label="Service guarantees">
                <div className="flex items-center" role="listitem">
                  <CheckCircle className="w-6 h-6 mr-3 text-green-300" aria-hidden="true" />
                  <span><strong>Available 24/7/365</strong> - Never closed, always here</span>
                </div>
                <div className="flex items-center" role="listitem">
                  <CheckCircle className="w-6 h-6 mr-3 text-green-300" aria-hidden="true" />
                  <span><strong>Payment Plans Available</strong> - Work with any budget</span>
                </div>
                <div className="flex items-center" role="listitem">
                  <CheckCircle className="w-6 h-6 mr-3 text-green-300" aria-hidden="true" />
                  <span><strong>Licensed & Bonded</strong> - Fully insured service</span>
                </div>
                <div className="flex items-center" role="listitem">
                  <CheckCircle className="w-6 h-6 mr-3 text-green-300" aria-hidden="true" />
                  <span><strong>All Counties Served</strong> - We travel anywhere</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LandingPage;